import { 
  Mail, Phone, Globe, User, Building, Calendar, DollarSign, Link2, FileText, ExternalLink 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function InfoRow({ icon: Icon, label, value, isLink = false, href = "" }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <span className="text-muted-foreground text-xs">{label}</span>
        {isLink ? (
          <a href={href} target="_blank" rel="noreferrer" className="text-primary hover:underline block truncate">
            {value}
          </a>
        ) : (
          <p className="truncate">{value || "—"}</p>
        )}
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, children }) {
  return (
    <Card>
      <CardHeader className="pb-2 px-3 sm:px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 sm:px-4 space-y-2">
        {children}
      </CardContent>
    </Card>
  );
}

export default function ActivationDetailsTab({ program }) {
  const fathomLinks = program?.fathom_links || [];
  
  return (
    <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
      {/* Client & Project Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <SectionCard icon={User} title="Client">
          <InfoRow icon={Mail} label="Email" value={program?.client_email} isLink href={`mailto:${program?.client_email}`} />
          <InfoRow icon={Phone} label="Phone" value={program?.client_phone} />
          <InfoRow icon={Globe} label="Timezone" value={program?.client_timezone} />
        </SectionCard>

        <SectionCard icon={Building} title="Project">
          <InfoRow icon={Building} label="Location" value={program?.project_address_full || `${program?.project_city || ''}, ${program?.project_state || ''}`} />
          <InfoRow icon={Calendar} label="Dates" value={`${formatDate(program?.start_window)} → ${formatDate(program?.end_window)}`} />
          <div className="flex gap-4 text-sm">
            <div>
              <span className="text-xs text-muted-foreground">Service:</span>
              <p>{program?.service_type || "—"}</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Links */}
      <SectionCard icon={Link2} title="Links & Scope">
        <div className="flex items-center gap-2 p-2 bg-secondary/30 rounded">
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <a 
            href={program?.signed_agreement_link} 
            target="_blank" 
            rel="noreferrer" 
            className="text-sm text-primary hover:underline truncate flex-1"
          >
            {program?.signed_agreement_link || "No agreement link"}
          </a>
          <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </div>
        
        {fathomLinks.length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-1">Fathom Recordings:</p>
            <div className="flex flex-wrap gap-2">
              {fathomLinks.map((link, i) => {
                const url = typeof link === 'object' ? link.url : link;
                return (
                  <a 
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 px-2 py-1 bg-secondary/30 rounded"
                  >
                    <ExternalLink className="w-3 h-3" /> #{i + 1}
                  </a>
                );
              })}
            </div>
          </div>
        )}
        
        {program?.scope_details && (
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-1">Scope:</p>
            <p className="text-sm bg-secondary/30 p-2 rounded">{program.scope_details}</p>
          </div>
        )}
      </SectionCard>

      {/* Payment */}
      <SectionCard icon={DollarSign} title="Payment">
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <span className="text-xs text-muted-foreground">Method</span>
            <p className="font-medium">{program?.payment_method || "—"}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Deposit</span>
            <p className="font-medium">${program?.deposit_amount || 0}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Paid</span>
            <p className="font-medium text-green-600">${program?.cash_collected || 0}</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
